import React from 'react';
import SearchableSelect from './SearchableSelect';
import { UploadIcon } from './Icons';
import { isRateTableEntryType, isDocumentEntryType, getDocumentEntryConfig } from '../utils/api';

const LightningIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
  </svg>
);

const WriteModePanel = ({
  mode,
  queryObj,
  onObjectChange,
  filteredObjects,
  loading,
  onPreviewClick,
  costStructureMode,
  onCostStructureModeChange,
  onValidateCostStructure,
}) => {
  const getUpdateRequirements = () => {
    const isRateTable = isRateTableEntryType(queryObj);
    const isDocEntry = isDocumentEntryType(queryObj);
    const docConfig = isDocEntry ? getDocumentEntryConfig(queryObj) : null;
    if (isRateTable) return { keyField: 'RATETABLEID', warning: '⚡ Rate table updates REPLACE all entries of this type.' };
    if (isDocEntry) return { keyField: `${docConfig.parentKeyField}`, lineField: `${docConfig.lineNoField}`, warning: null };
    return { keyField: 'RECORDNO', warning: null };
  };

  const updateReqs = mode === 'update' ? getUpdateRequirements() : null;
  const isCostStructure = mode === 'create' && costStructureMode;

  return (
    <div className={`bg-gray-800 p-4 rounded shadow-sm border ${mode === 'update' ? 'border-red-500/30' : 'border-green-500/30'} mb-6`}>
      <div className={`flex items-center gap-2 mb-4 ${mode === 'update' ? 'text-red-400 bg-red-500/10' : 'text-green-400 bg-green-500/10'} p-2 rounded border ${mode === 'update' ? 'border-red-500/20' : 'border-green-500/20'}`}>
        <LightningIcon />
        <span className="text-xs font-bold uppercase">{mode} Mode Active</span>
      </div>

      {mode === 'create' && (
        <label className="flex items-start gap-2 mb-4 cursor-pointer">
          <input
            type="checkbox"
            checked={!!costStructureMode}
            onChange={(e) => onCostStructureModeChange(e.target.checked)}
            className="mt-1 accent-amber-400"
          />
          <span className="text-sm text-gray-300">
            <span className="font-bold">Cost Structure mode</span>
            <span className="block text-xs text-gray-500 mt-0.5">
              Validate and create missing tasks and cost types from a list of Project/Cost Code/Cost Type combinations.
            </span>
          </span>
        </label>
      )}

      {!isCostStructure && (
        <div className="mb-6">
          <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Target Object</label>
          <SearchableSelect
            options={filteredObjects.map(o => ({ id: o.id, label: o.name }))}
            value={queryObj}
            onChange={onObjectChange}
          />
          <p className="text-[10px] text-gray-500 mt-1">
            {mode === 'update' ? 'Updates' : 'Creates'} will be sent to &lt;{queryObj}&gt;
          </p>
        </div>
      )}

      <div className="space-y-3">
        {isCostStructure ? (
          <>
            <p className="text-sm text-gray-300">
              Select the range in Excel (headers + data rows) with these three columns:
            </p>
            <ul className="text-xs text-gray-400 space-y-1 ml-1">
              <li>• <span className="text-gray-300 font-medium">Project</span> <span className="text-gray-500">(also: PROJECTID, Project ID)</span></li>
              <li>• <span className="text-gray-300 font-medium">Cost Code</span> <span className="text-gray-500">(also: TASKID, TASK ID, CostCode, Cost Code Id, COSTCODEID)</span></li>
              <li>• <span className="text-gray-300 font-medium">Cost Type</span> <span className="text-gray-500">(also: COSTTYPEID, Cost Type ID, COSTTYPE)</span></li>
            </ul>
            <p className="text-[11px] text-gray-500">
              Column order doesn't matter. Capitalization doesn't matter. Other columns will be ignored.
            </p>
            <button
              onClick={onValidateCostStructure}
              disabled={loading}
              className="w-full py-3 px-4 bg-amber-500 hover:bg-amber-400 disabled:bg-gray-600 text-gray-900 rounded-md shadow-sm text-sm font-bold flex items-center justify-center gap-2"
            >
              <LightningIcon /> Validate Cost Structure
            </button>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-300">
              Select the range in Excel you want to {mode}.<br /><br /><strong className="text-white">Requirements:</strong>
            </p>
            <ul className="list-disc ml-4 text-xs text-gray-400">
              <li>Selection must include headers.</li>
              {mode === 'update' && updateReqs && (
                <li>One header must be <code className="bg-gray-700 px-1 rounded text-yellow-400">{updateReqs.keyField}</code>.</li>
              )}
              {mode === 'update' && updateReqs?.lineField && (
                <li>One header must be <code className="bg-gray-700 px-1 rounded text-yellow-400">{updateReqs.lineField}</code> (0-based, will be converted to 1-based).</li>
              )}
              <li>Results will be written to the right of your selection.</li>
              {updateReqs?.warning && <li className="text-orange-400 font-medium">{updateReqs.warning}</li>}
            </ul>
            <button
              onClick={onPreviewClick}
              disabled={loading}
              className={`w-full py-3 px-4 ${mode === 'update' ? 'bg-red-500 hover:bg-red-400' : 'bg-green-500 hover:bg-green-400'} text-gray-900 rounded-md shadow-sm text-sm font-bold flex items-center justify-center gap-2`}
            >
              <UploadIcon /> Preview {mode === 'update' ? 'Update' : 'Create'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default WriteModePanel;